import { useLazyQuery, useQuery } from '@apollo/client'
import { Hidden, useTheme, useMediaQuery } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { ACCOMMODATION_NAME_QUERY } from '../../graphql/accommodation'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import ratePlanPagesRoleManagement from '../../staticRoleManagement/ratePlanRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeFilters } from '../../types/roomtype'
import RateplansHeader from './RateplansHeader'
import { RateplansTable } from './RateplansTable'
import { RateplansTableMobile } from './RateplansTableMobile'
import useStyles from './styles/index.styles'

const RateplansList: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [roomType, setroomType] = useState<any>()
  const params = useParams<{ accommodationID: string; propertyCode: string }>()
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filters, setFilters] = useState<RoomTypeFilters>({
    type: 'all',
    existing: 'all',
    activation: 'all'
  })
  const [filteredRatePlans, setfilteredRatePlans] = useState<RatePlanBrief[]>(
    []
  )

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const hasAccessToViewRatePlanTabs = ratePlanPagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/rate-plans/list'
    )
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const [getRatePlans, { data: ratePlanData, loading, refetch }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onError: (err) => {
        toast.error(err.message)
      }
    })

  const { data: accommodationData } = useQuery<{
    accommodationName: {
      name: string
    }
  }>(ACCOMMODATION_NAME_QUERY, {
    variables: {
      propertyCode: params.propertyCode
    }
  })

  useEffect(() => {
    if (params.accommodationID) {
      getRatePlans({
        variables: {
          propertyCode: params.propertyCode,
          roomTypeCode: roomType && roomType.value,
          showDeletedRatePlans: isRoleAdmin ? true : false,
          showDeactivedRatePlans: true,
          showLog: true
        },
        fetchPolicy: 'network-only'
      })
    }
  }, [roomType && roomType.value])

  useEffect(() => {
    setPage(1)
    const fullRatePlansList = ratePlanData?.ratePlans
      ? ratePlanData?.ratePlans
      : []
    const newFilteredRoomtypes = matchSorter(
      fullRatePlansList,
      searchInputValue,
      {
        keys: ['name', 'displayRatePlanCode', 'type'],
        sorter: (rankedItems) =>
          rankedItems.sort((a, b) => {
            return a.item.name > b.item.name ? 1 : -1
          })
      }
    )

    setfilteredRatePlans(newFilteredRoomtypes)
    setcount(newFilteredRoomtypes.length)
  }, [searchInputValue, loading])

  useEffect(() => {
    if (ratePlanData) {
      setPage(1)
      const { type, existing, activation } = filters

      const tempList = ratePlanData?.ratePlans.filter((item) => {
        // Filter by type
        if (type === 'virtual' && item.type !== 'Virtual') return false
        if (type === 'physical' && item.type !== 'Physical') return false

        // Filter by existing status
        if (existing === 'deleted' && !item.deleted) return false
        if (existing === 'existing' && item.deleted) return false

        // Filter by activation status
        if (activation === 'deactive' && item.active) return false
        if (activation === 'active' && !item.active) return false

        // If all conditions are passed, keep the item
        return true
      })

      setfilteredRatePlans(tempList)
      setcount(tempList.length)
    }
  }, [filters, loading])

  return (
    <div>
      <PageHeader title={getTranslate('RatePlan')} />
      <RateplansHeader
        setroomType={setroomType}
        roomType={roomType}
        inputValue={searchInputValue}
        setinputVal={setsearchInputValue}
        accommodationName={accommodationData?.accommodationName.name || ''}
        isRoleAdmin={isRoleAdmin}
        filters={filters}
        setFilters={setFilters}
      />
      <Hidden smDown>
        <RateplansTable
          hasAccessToViewRatePlanTabs={hasAccessToViewRatePlanTabs}
          reloadData={refetch}
          data={filteredRatePlans.slice((page - 1) * perPage, page * perPage)}
          loading={loading}
          isRoleAdmin={isRoleAdmin}
          setFilters={setFilters}
        />
      </Hidden>
      <Hidden mdUp>
        <RateplansTableMobile
          hasAccessToViewRatePlanTabs={hasAccessToViewRatePlanTabs}
          data={filteredRatePlans.slice((page - 1) * perPage, page * perPage)}
          loading={loading}
          roomTypeid={roomType && roomType.value}
        />
      </Hidden>
      {ratePlanData?.ratePlans && ratePlanData?.ratePlans.length > 0 && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={numberOfPages}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </div>
      )}
    </div>
  )
}

export default RateplansList
